import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "results", "chipsContainer", "template"]
  static values = { searchUrl: String }

  connect() {
    this.timeout = null
    this.fetchId = 0
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
    this.fetchId++
  }

  handleInput() {
    if (this.timeout) clearTimeout(this.timeout)

    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) {
      this.clearResults()
      return
    }

    this.timeout = setTimeout(() => this.fetchResults(query), 200)
  }

  async fetchResults(query) {
    const thisFetch = ++this.fetchId
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("query", query)
    this.selectedIds().forEach(id => url.searchParams.append("exclude_ids[]", id))

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (thisFetch !== this.fetchId) return
      if (!response.ok) {
        this.clearResults()
        return
      }
      const items = await response.json()
      if (thisFetch !== this.fetchId) return
      this.renderResults(items)
    } catch (e) {
      console.error("[catalog-picker] search failed:", e)
      if (thisFetch === this.fetchId) this.clearResults()
    }
  }

  renderResults(items) {
    this.clearResults()
    if (items.length === 0) {
      const empty = document.createElement("div")
      empty.className = "list-group-item text-muted small"
      empty.textContent = "No matching catalogs."
      this.resultsTarget.appendChild(empty)
      return
    }

    items.forEach(item => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "list-group-item list-group-item-action"
      button.dataset.action = "click->catalog-picker#selectResult"
      button.dataset.id = item.id
      button.dataset.name = item.name
      button.dataset.ownerLabel = item.owner_label

      const nameEl = document.createElement("strong")
      nameEl.textContent = item.name

      const ownerEl = document.createElement("span")
      ownerEl.className = "text-muted small ms-2"
      ownerEl.textContent = item.owner_label

      button.appendChild(nameEl)
      button.appendChild(ownerEl)
      this.resultsTarget.appendChild(button)
    })
  }

  selectResult(event) {
    const button = event.currentTarget
    const { id, name, ownerLabel } = button.dataset

    if (this.selectedIds().includes(id)) {
      this.clearSearch()
      return
    }

    const fragment = this.templateTarget.content.cloneNode(true)
    fragment.querySelector("[data-catalog-id]").dataset.catalogId = id
    fragment.querySelector("[data-name]").textContent = name
    fragment.querySelector("[data-owner-label]").textContent = ownerLabel
    fragment.querySelector("input[type=hidden]").value = id

    this.chipsContainerTarget.appendChild(fragment)
    this.clearSearch()
  }

  removeChip(event) {
    const chip = event.currentTarget.closest("[data-catalog-id]")
    if (chip) chip.remove()
  }

  handleKeydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    const firstResult = this.resultsTarget.querySelector("button[data-id]")
    if (firstResult) firstResult.click()
  }

  selectedIds() {
    return Array.from(this.chipsContainerTarget.querySelectorAll("[data-catalog-id]"))
      .map(el => el.dataset.catalogId)
  }

  clearSearch() {
    this.searchInputTarget.value = ""
    this.clearResults()
  }

  clearResults() {
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild)
    }
  }
}
